import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBrandVendorOrders } from '../../actions/brandVendorOrdersActions';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import useOrderListFilters from '../../hooks/useOrderListFilters';
import OrderListFilterBar from '../../components/Orders/OrderListFilterBar';
import { toast } from 'react-toastify';
import { socket } from '../../utilities/socket';
import {
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    makeStyles,
    TablePagination,
    Button,
    Box,
    Modal,
    Backdrop,
    Fade,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { getUserFromLocalStorage } from '../../utilities/user.js';
import { getTokenFromLocalStorage } from '../../utilities/tokenUtils.js';
import { useGate } from '../../components/team/Gated';
import { useVendorAccess } from '../../hooks/useVendorAccess';
import useVendorOrderRooms from '../../hooks/useVendorOrderRooms';
import { useBranchScope, useVendorLocations } from '../../context/BranchScopeContext';
import { branchAwareMessage, orderLocationName, vendorOwnerIdOf } from '../../utilities/vendorScope';
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL;

/* ─── Power-BI palette ───────────────────────────────────────── */
const PBI = {
    navy:       '#1a2f4e',
    navyLight:  '#243752',
    accent:     '#0a578b',
    accentSoft: '#e8f3fb',
    yellow:     '#f2c94c',
    border:     '#e2e8f0',
    bg:         '#f0f4f8',
    text:       '#1e293b',
    muted:      '#64748b',
    white:      '#ffffff',
};

const STATUS_META = {
    PROCESSING:         { dot: '#f59e0b', bg: '#fef3c7', text: '#92400e' },
    ASSIGNED:           { dot: '#6366f1', bg: '#ede9fe', text: '#4338ca' },
    DISPATCHED:         { dot: '#3b82f6', bg: '#dbeafe', text: '#1e40af' },
    'OUT FOR DELIVERY': { dot: '#06b6d4', bg: '#cffafe', text: '#0e7490' },
    SHIPPED:            { dot: '#06b6d4', bg: '#cffafe', text: '#0e7490' },
    DELIVERED:          { dot: '#22c55e', bg: '#dcfce7', text: '#166534' },
    CANCELLED:          { dot: '#f43f5e', bg: '#fee2e2', text: '#991b1b' },
    REJECTED:           { dot: '#f43f5e', bg: '#fee2e2', text: '#991b1b' },
    /* legacy title-case fallbacks */
    Processing:         { dot: '#f59e0b', bg: '#fef3c7', text: '#92400e' },
    Assigned:           { dot: '#6366f1', bg: '#ede9fe', text: '#4338ca' },
    Dispatched:         { dot: '#3b82f6', bg: '#dbeafe', text: '#1e40af' },
    Delivered:          { dot: '#22c55e', bg: '#dcfce7', text: '#166534' },
    Cancelled:          { dot: '#f43f5e', bg: '#fee2e2', text: '#991b1b' },
    Rejected:           { dot: '#f43f5e', bg: '#fee2e2', text: '#991b1b' },
};

const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || { dot: '#94a3b8', bg: '#f1f5f9', text: '#475569' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 20,
            background: meta.bg, color: meta.text,
            fontSize: 12, fontWeight: 600, letterSpacing: 0.3,
        }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.dot, flexShrink: 0 }} />
            {status || '—'}
        </span>
    );
};

const QUOTE_STATUS_META = {
    OPEN:      { dot: '#f59e0b', bg: '#fef3c7', text: '#92400e' },
    CLOSED:    { dot: '#94a3b8', bg: '#f1f5f9', text: '#475569' },
    CANCELLED: { dot: '#f43f5e', bg: '#fee2e2', text: '#991b1b' },
    SENT:      { dot: '#3b82f6', bg: '#dbeafe', text: '#1e40af' },
    ACCEPTED:  { dot: '#22c55e', bg: '#dcfce7', text: '#166534' },
    EXPIRED:   { dot: '#64748b', bg: '#e2e8f0', text: '#334155' },
    /* These four are in the model's enum and were missing here, so every
       counter-offer, revision request, rejection and superseded quote fell to
       the grey default and read as the same nothing-in-particular. */
    COUNTER_OFFERED:    { dot: '#f97316', bg: '#ffedd5', text: '#9a3412' },
    REVISION_REQUESTED: { dot: '#f97316', bg: '#ffedd5', text: '#9a3412' },
    REJECTED:           { dot: '#f43f5e', bg: '#fee2e2', text: '#991b1b' },
    SUPERSEDED:         { dot: '#94a3b8', bg: '#f1f5f9', text: '#475569' },
};

/**
 * THE REQUEST'S REAL STATE, and what the vendor can do about it.
 *
 * This table used to render the legacy `status` field, which the server
 * collapses to "CLOSED" for every lifecycle except AWAITING_VENDOR and
 * CANCELLED. The effect was that a request went grey and lost its only button
 * the moment the vendor answered it, and a buyer's counter-offer landed in a
 * row that said CLOSED with nothing to click. The only way to answer a
 * counter-offer was the deep link in the original arrival email. The canonical
 * field is `lifecycleStatus`, which the API has been returning all along
 * through `requestForClient`.
 *
 * `label` is what the vendor reads, `action` is the button, and `needsVendor`
 * is what the counters above the table add up. Keep this in step with
 * REQUEST_LIFECYCLE in services/brandQuoteLifecycleService.js.
 */
const REQUEST_LIFECYCLE_META = {
    AWAITING_VENDOR: { label: 'Awaiting your quote', action: 'Send quote',    needsVendor: true,  ...{ dot: '#f59e0b', bg: '#fef3c7', text: '#92400e' } },
    QUOTED:          { label: 'Quote sent',          action: 'View',          needsVendor: false, ...{ dot: '#3b82f6', bg: '#dbeafe', text: '#1e40af' } },
    NEGOTIATING:     { label: 'Buyer replied',       action: 'Respond',       needsVendor: true,  ...{ dot: '#f97316', bg: '#ffedd5', text: '#9a3412' } },
    ACCEPTED:        { label: 'Accepted',            action: 'View',          needsVendor: false, ...{ dot: '#22c55e', bg: '#dcfce7', text: '#166534' } },
    FULFILLED:       { label: 'Fulfilled',           action: 'View',          needsVendor: false, ...{ dot: '#22c55e', bg: '#dcfce7', text: '#166534' } },
    DECLINED:        { label: 'Declined',            action: null,            needsVendor: false, ...{ dot: '#f43f5e', bg: '#fee2e2', text: '#991b1b' } },
    CANCELLED:       { label: 'Cancelled by buyer',  action: null,            needsVendor: false, ...{ dot: '#f43f5e', bg: '#fee2e2', text: '#991b1b' } },
    EXPIRED:         { label: 'Expired',             action: null,            needsVendor: false, ...{ dot: '#64748b', bg: '#e2e8f0', text: '#334155' } },
};

/* Rows written before `lifecycleStatus` existed carry only the legacy field.
   Read exactly as the server's `getRequestLifecycle` reads them, so a count and
   the list beneath it can never disagree about the same request. */
const lifecycleOf = (request) => {
    if (request?.lifecycleStatus) return request.lifecycleStatus;
    if (request?.status === 'CANCELLED') return 'CANCELLED';
    if (request?.status === 'CLOSED') return 'QUOTED';
    return 'AWAITING_VENDOR';
};

const requestMetaOf = (request) =>
    REQUEST_LIFECYCLE_META[lifecycleOf(request)] || REQUEST_LIFECYCLE_META.AWAITING_VENDOR;

const needsVendorAction = (request) => requestMetaOf(request).needsVendor === true;

const RequestLifecycleBadge = ({ request }) => {
    const meta = requestMetaOf(request);
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 20,
            background: meta.bg, color: meta.text,
            fontSize: 12, fontWeight: 600, letterSpacing: 0.3,
        }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.dot, flexShrink: 0 }} />
            {meta.label}
        </span>
    );
};

const QuoteStatusBadge = ({ status }) => {
    const meta = QUOTE_STATUS_META[status] || { dot: '#94a3b8', bg: '#f1f5f9', text: '#475569' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 20,
            background: meta.bg, color: meta.text,
            fontSize: 12, fontWeight: 600, letterSpacing: 0.3,
        }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.dot, flexShrink: 0 }} />
            {status || '—'}
        </span>
    );
};

const KpiCard = ({ label, value, accent }) => (
    <div style={{
        background: PBI.white, border: `1px solid ${PBI.border}`,
        borderRadius: 10, padding: '16px 20px',
        borderLeft: `4px solid ${accent}`,
        minWidth: 130, flex: '1 1 130px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    }}>
        <div style={{ fontSize: 11, color: PBI.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 700, color: PBI.text, lineHeight: 1 }}>{value}</div>
    </div>
);


const useStyles = makeStyles(() => ({
    loadingContainer: {
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh',
    },
    tableRow: {
        cursor: 'pointer',
        transition: 'background 0.15s',
        '&:hover': { background: PBI.accentSoft },
    },
    noOrdersContainer: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '55vh', textAlign: 'center',
        padding: 32,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    },
    noOrdersIcon: {
        width: 240, height: 240, marginBottom: 24,
        animation: '$float 3s ease-in-out infinite',
    },
    '@keyframes float': {
        '0%,100%': { transform: 'translateY(0)' },
        '50%':      { transform: 'translateY(-18px)' },
    },
    modalBox: {
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: PBI.white, borderRadius: 12,
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
        padding: '28px 32px', width: '100%', maxWidth: 580,
        maxHeight: '90vh', overflowY: 'auto',
        outline: 'none',
    },
}));

const TABS = { ORDERS: 'ORDERS', QUOTES: 'QUOTES' };

const BrandOrderTable = () => {
    const classes = useStyles();
    const token = getTokenFromLocalStorage();
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const location = useLocation();
    /* Quotes are head office's (4 Oct 2026): a member limited to some
       branches gets the Orders tab only, and the server refuses the quote
       reads for them with BRANCH_SCOPE_HQ_ONLY. */
    const { isBranchScoped } = useVendorAccess();
    const { outletIdParam } = useBranchScope();
    const showQuotes = !isBranchScoped;
    const initialTab = showQuotes && new URLSearchParams(location.search).get('tab') === 'quotes' ? TABS.QUOTES : TABS.ORDERS;

    const [vendorId, setVendorId] = useState(null);
    const [brandId, setBrandId] = useState(null);
    const [activeTab, setActiveTab] = useState(initialTab);
    /* Status and payment-method filters live in the URL (the dashboard cards
       open this list with one set, utilities/orderDeepLinks.js) and are
       applied on the SERVER, so the pending card's three statuses select the
       same rows across pages that the card counted. They used to filter the
       ten rows on the page. */
    const listFilters = useOrderListFilters();
    /* Location filter: '' is every location this login may see. Server-side
       (?outletId=), because a location's orders span pages. It starts on the
       branch picked in the navbar, and lists the locations from /locations
       (main included), clamped to the login's branches (4 Oct 2026). */
    const [outletFilter, setOutletFilter] = useState(() => outletIdParam || '');
    const orderFilters = { ...(outletFilter ? { outletId: outletFilter } : {}), ...listFilters.filters };
    // Every server-side filter as one string: a change restarts at page 1.
    const filterIdentity = `${outletFilter}|${listFilters.filterKey}`;

    /* ── admin brand selector ── */
    const [allBrands, setAllBrands] = useState([]);
    const [selectedAdminBrand, setSelectedAdminBrand] = useState(null);

    /* ── quotes state ── */
    const [requests, setRequests] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [quotesLoading, setQuotesLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [quoteForm, setQuoteForm] = useState({ lineItems: [{ description: '', quantity: 1, unitPrice: 0 }], notes: '', validUntil: '', currency: 'UGX', paymentTerms: '', leadTime: '', shippingCost: 0, vatPct: 0 });
    const [submittingQuote, setSubmittingQuote] = useState(false);

    const dispatch = useDispatch();
    const { orders, loading, error, currentPage, numberOfPages } = useSelector((s) => s.brandVendorOrders);
    const history = useHistory();
    const user = getUserFromLocalStorage();
    const isSuperAdmin = user?.result?.user_role === 'SUPERUSER' || user?.result?.role === 'SUPERUSER';

    /* For superadmin: fetch all brands and let them pick one */
    useEffect(() => {
        if (!isSuperAdmin || !token) return;
        axios.get(`${API_URL}/api/brands`, { headers: authHeaders })
            .then((res) => {
                const list = res.data?.data || res.data || [];
                const arr = Array.isArray(list) ? list : [];
                setAllBrands(arr);
                if (arr.length > 0) setSelectedAdminBrand(arr[0]);
            })
            .catch(() => {});
    }, [isSuperAdmin, token]);

    /* When admin selects a brand, set vendorId = brand.owner and brandId = brand._id */
    useEffect(() => {
        if (!isSuperAdmin) return;
        if (selectedAdminBrand) {
            setVendorId(String(selectedAdminBrand.owner?._id || selectedAdminBrand.owner));
            setBrandId(String(selectedAdminBrand._id));
        }
    }, [selectedAdminBrand, isSuperAdmin]);

    /* For brand owners: the owner's id (a team member works for its parent). */
    useEffect(() => {
        if (!isSuperAdmin && user) setVendorId(vendorOwnerIdOf(user));
    }, []);

    /* resolve brandId from vendorId (brand owner path only) */
    useEffect(() => {
        if (isSuperAdmin || !vendorId || !token) return;
        axios.get(`${API_URL}/api/brands`, { params: { vendorId }, headers: authHeaders })
            .then((res) => {
                const list = res.data?.data || [];
                if (list.length > 0) setBrandId(String(list[0]._id));
            })
            .catch(() => {});
    }, [vendorId, token]);

    /* The selected brand's locations feed the filter. A brand without
       branches gets no dropdown at all. */
    const brandLocations = useVendorLocations(vendorId, { parentId: brandId });
    /* With more than one location, each order says which branch it is for
       (a brand order is fulfilled from fulfilmentOutletId; none is the main
       location). A brand with one location sees the table it always had. */
    const showBranchColumn = brandLocations.hasBranches && brandLocations.locations.length > 1;
    const branchNameOf = useCallback(
        (id) => brandLocations.locations.find((l) => l.id === id)?.name || null,
        [brandLocations.locations]
    );
    const lastBrandId = useRef(brandId);
    useEffect(() => {
        if (lastBrandId.current === brandId) return;
        lastBrandId.current = brandId;
        setOutletFilter(outletIdParam || '');
    }, [brandId, outletIdParam]);

    /* socket — live refresh when a new brand order arrives.
     *
     * BOTH rooms, matching the server's fan-out (WIRE_CONTRACT §8). The server
     * used to emit to `brandVendor:${brandId || vendorId}`, which for every POS
     * order resolved to the vendor USER _id — a different document from the
     * Brand _id this table was joining, so the till's order landed in a room
     * with nobody in it and only appeared on a manual refresh. The server now
     * fans out to both keys; this joins both so neither side has to guess.
     *
     * It also covers the multi-brand owner: `brandId` above is the FIRST brand
     * the account owns, so a terminal assigned to the second one would emit on
     * a room this table never joined. The vendor room catches it.
     */
    /* One join for every room (4 Oct 2026): the server picks the brand rooms
       for head office, or the branch rooms for a branch member. The two
       generic joins are the fallback for a server older than this page. */
    useVendorOrderRooms(socket, vendorId, {
        fallback: (s) => {
            if (brandId) s.emit('join', `brandVendor:${brandId}`);
            if (vendorId) s.emit('join', `brandVendor:${vendorId}`);
        },
    });
    useEffect(() => {
        if (!brandId || !vendorId) return;
        const refresh = () => dispatch(fetchBrandVendorOrders(vendorId, currentPage, 10, orderFilters));
        socket.on('newBrandOrder', refresh);
        socket.on('newOrder', refresh);
        socket.on('restaurantOrderStatusChanged', refresh);
        return () => {
            socket.off('newBrandOrder', refresh);
            socket.off('newOrder', refresh);
            socket.off('restaurantOrderStatusChanged', refresh);
        };
    }, [brandId, vendorId, currentPage, filterIdentity, dispatch]);

    /* A changed filter (location, status or payment method) restarts at page
       1: the page the operator was on may not exist for the narrower list. */
    const lastFilterIdentity = useRef(filterIdentity);
    useEffect(() => {
        if (!vendorId) return;
        const filterChanged = lastFilterIdentity.current !== filterIdentity;
        lastFilterIdentity.current = filterIdentity;
        dispatch(fetchBrandVendorOrders(vendorId, filterChanged ? 1 : currentPage, 10, orderFilters));
    }, [dispatch, vendorId, currentPage, filterIdentity]);

    /* ── quotes fetch ── */
    const fetchQuotes = useCallback(async (id) => {
        if (!id || !token) return;
        setQuotesLoading(true);
        try {
            const [reqRes, qRes] = await Promise.all([
                axios.get(`${API_URL}/api/brands/${id}/quote-requests`, { headers: authHeaders }),
                axios.get(`${API_URL}/api/brands/${id}/quotes`, { headers: authHeaders }),
            ]);
            setRequests(reqRes.data.data || []);
            setQuotes(qRes.data.data || []);
        } catch (e) {
            toast.error(branchAwareMessage(e, 'Failed to load quotes'));
        } finally {
            setQuotesLoading(false);
        }
    }, [token]);

    // Fetch quotes as soon as brandId resolves (for badge count), and refetch on tab switch
    useEffect(() => {
        if (brandId && showQuotes) fetchQuotes(brandId);
    }, [brandId, fetchQuotes, showQuotes]);

    useEffect(() => {
        if (activeTab === TABS.QUOTES && brandId && showQuotes) fetchQuotes(brandId);
    }, [activeTab]);

    // Vendor team permissions: sending a quote is quotes:manage.
    const quoteGate = useGate('quotes:manage');

    const handleManage = (orderId) => {
        history.push(isSuperAdmin
            ? `/admin/brandOrderDetails/${orderId}`
            : `/admin/brand-order-details/${orderId}`
        );
    };

    /* ── quote form helpers ── */
    const addLineItem = () => setQuoteForm((f) => ({ ...f, lineItems: [...f.lineItems, { description: '', quantity: 1, unitPrice: 0 }] }));
    const updateLineItem = (idx, key, val) => setQuoteForm((f) => { const li = [...f.lineItems]; li[idx] = { ...li[idx], [key]: val }; return { ...f, lineItems: li }; });
    const removeLineItem = (idx) => setQuoteForm((f) => ({ ...f, lineItems: f.lineItems.filter((_, i) => i !== idx) }));
    const subtotal = quoteForm.lineItems.reduce((sum, li) => sum + (Number(li.quantity) || 0) * (Number(li.unitPrice) || 0), 0);
    const vatAmount = Math.round(subtotal * (Number(quoteForm.vatPct) || 0) / 100);
    const totalAmount = subtotal + vatAmount + (Number(quoteForm.shippingCost) || 0);

    const submitQuoteResponse = async () => {
        if (!selectedRequest) return;
        setSubmittingQuote(true);
        try {
            const structuredNotes = [
                quoteForm.paymentTerms && `Payment terms: ${quoteForm.paymentTerms}`,
                quoteForm.leadTime    && `Estimated lead time: ${quoteForm.leadTime}`,
                quoteForm.notes       && quoteForm.notes,
            ].filter(Boolean).join('\n');

            const allLineItems = [
                ...quoteForm.lineItems.map((li) => ({ ...li, quantity: Number(li.quantity), unitPrice: Number(li.unitPrice) })),
                ...(Number(quoteForm.shippingCost) > 0 ? [{ description: 'Shipping / Freight', quantity: 1, unitPrice: Number(quoteForm.shippingCost) }] : []),
                ...(Number(quoteForm.vatPct) > 0    ? [{ description: `VAT (${quoteForm.vatPct}%)`, quantity: 1, unitPrice: vatAmount }] : []),
            ];

            await axios.post(
                `${API_URL}/api/brands/quote-requests/${selectedRequest._id}/quotes`,
                { lineItems: allLineItems, totalAmount, currency: quoteForm.currency, validUntil: quoteForm.validUntil, notes: structuredNotes },
                { headers: authHeaders }
            );
            toast.success('Quote sent to buyer.');
            setSelectedRequest(null);
            setQuoteForm({ lineItems: [{ description: '', quantity: 1, unitPrice: 0 }], notes: '', validUntil: '', currency: 'UGX', paymentTerms: '', leadTime: '', shippingCost: 0, vatPct: 0 });
            fetchQuotes(brandId);
        } catch (e) {
            toast.error(e.response?.data?.message || 'Failed to send quote.');
        } finally {
            setSubmittingQuote(false);
        }
    };

    /* ── KPI counts ── */
    const kpis = {
        total:      orders.length,
        processing: orders.filter((o) => o.delivery_status === 'PROCESSING').length,
        delivered:  orders.filter((o) => o.delivery_status === 'DELIVERED').length,
        cancelled:  orders.filter((o) => ['CANCELLED', 'REJECTED'].includes(o.delivery_status)).length,
    };

    /* The rows are already the filtered set: the server applied the status
       and payment-method filters before paging. */
    const visibleOrders = orders;
    const STATUS_PILLS = ['PROCESSING', 'ASSIGNED', 'DISPATCHED', 'DELIVERED', 'CANCELLED'].map((value) => ({
        value, label: value === 'CANCELLED' ? 'Cancelled' : value.charAt(0) + value.slice(1).toLowerCase(),
    }));

    if (loading && activeTab === TABS.ORDERS) return <div className={classes.loadingContainer}><CircularProgress style={{ color: PBI.accent }} /></div>;
    if (error && activeTab === TABS.ORDERS)   return <Alert severity="error" style={{ margin: 24 }}>Error: {error}</Alert>;

    return (
        <div style={{ padding: '0 0 32px', fontFamily: "'Segoe UI', Inter, sans-serif", background: PBI.bg, minHeight: '100vh' }}>

            {/* ── Header bar ── */}
            <div style={{
                background: `linear-gradient(135deg, ${PBI.navy} 0%, ${PBI.navyLight} 100%)`,
                padding: '20px 28px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>{activeTab === TABS.ORDERS ? '📦' : '💬'}</span>
                        <Typography style={{ color: PBI.white, fontWeight: 700, fontSize: 20, margin: 0 }}>
                            {activeTab === TABS.ORDERS ? 'Brand Orders' : 'Quote Requests & Quotes'}
                        </Typography>
                    </div>
                    <Typography style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 2 }}>
                        {activeTab === TABS.ORDERS
                            ? 'Manage and track all orders placed for your brand'
                            : 'Review quote requests and send formal quotes to buyers'}
                    </Typography>
                    {/* ── Admin brand picker ── */}
                    {isSuperAdmin && allBrands.length > 0 && (
                        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Typography style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, whiteSpace: 'nowrap' }}>
                                Viewing brand:
                            </Typography>
                            <AppSelect
                                value={selectedAdminBrand?._id || ''}
                                onChange={(e) => {
                                    const brand = allBrands.find((b) => String(b._id) === e.target.value);
                                    if (brand) setSelectedAdminBrand(brand);
                                }}
                                style={{
                                    padding: '4px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    background: 'rgba(255,255,255,0.12)', color: PBI.white,
                                    cursor: 'pointer', minWidth: 200,
                                }}
                            >
                                {allBrands.map((b) => (
                                    <option key={b._id} value={String(b._id)} style={{ color: PBI.navy, background: PBI.white }}>
                                        {b.tradingName || b.name || b._id}
                                    </option>
                                ))}
                            </AppSelect>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {/* Tab switcher */}
                    {(showQuotes ? [TABS.ORDERS, TABS.QUOTES] : [TABS.ORDERS]).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '7px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                                cursor: 'pointer', border: 'none', transition: 'all 0.15s',
                                background: activeTab === tab ? PBI.yellow : 'rgba(255,255,255,0.12)',
                                color: activeTab === tab ? PBI.navy : PBI.white,
                            }}
                        >
                            {tab === TABS.ORDERS
                                ? `Orders${orders.length > 0 ? ` (${orders.length})` : ''}`
                                : `Quotes${requests.filter(needsVendorAction).length > 0 ? ` (${requests.filter(needsVendorAction).length})` : ''}`}
                        </button>
                    ))}
                    <button
                        onClick={() => activeTab === TABS.ORDERS
                            ? vendorId && dispatch(fetchBrandVendorOrders(vendorId, currentPage, 10, orderFilters))
                            : brandId && fetchQuotes(brandId)}
                        style={{
                            background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
                            color: PBI.white, borderRadius: 8, padding: '7px 16px',
                            fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        ↻ Refresh
                    </button>
                </div>
            </div>

            <div style={{ padding: '20px 28px' }}>

                {/* ══════════════ QUOTES TAB ══════════════ */}
                {activeTab === TABS.QUOTES && (
                    quotesLoading ? (
                        <div className={classes.loadingContainer}><CircularProgress style={{ color: PBI.accent }} /></div>
                    ) : (
                        <>
                            {/* Quote request KPIs */}
                            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
                                <KpiCard label="Needs your reply" value={requests.filter(needsVendorAction).length}       accent="#f59e0b" />
                                <KpiCard label="Quotes Sent"    value={quotes.length}                                        accent={PBI.accent} />
                                <KpiCard label="Accepted"       value={quotes.filter(q => q.status === 'ACCEPTED').length}  accent="#22c55e" />
                                <KpiCard label="Expired"        value={quotes.filter(q => q.status === 'EXPIRED').length}   accent="#94a3b8" />
                            </div>

                            {/* ── Quote Requests table ── */}
                            <Paper elevation={0} style={{ borderRadius: 12, border: `1px solid ${PBI.border}`, overflow: 'hidden', marginBottom: 24 }}>
                                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${PBI.border}`, background: PBI.white, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography style={{ fontWeight: 700, fontSize: 14, color: PBI.text }}>Quote Requests</Typography>
                                    <span style={{ fontSize: 12, color: PBI.muted }}>{requests.length} total</span>
                                </div>
                                {requests.length === 0 ? (
                                    <div style={{ padding: '32px 20px', textAlign: 'center', color: PBI.muted, fontSize: 14 }}>No quote requests yet.</div>
                                ) : (
                                    <TableContainer>
                                        <Table style={{ minWidth: 680 }}>
                                            <TableHead>
                                                <TableRow style={{ background: PBI.navy }}>
                                                    {['Status', 'Subject', 'Specs', 'Buyer', 'Deadline', 'Submitted', ''].map((h) => (
                                                        <TableCell key={h} style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: 11, letterSpacing: 0.7, textTransform: 'uppercase', padding: '13px 16px', borderBottom: 'none' }}>{h}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {requests.map((r, idx) => (
                                                    <TableRow key={r._id} className={classes.tableRow} style={{ background: idx % 2 === 0 ? PBI.white : '#f8fafc' }}>
                                                        <TableCell style={{ padding: '12px 16px' }}><RequestLifecycleBadge request={r} /></TableCell>
                                                        <TableCell style={{ fontWeight: 600, color: PBI.text, padding: '12px 16px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</TableCell>
                                                        <TableCell style={{ fontSize: 12, color: PBI.muted, padding: '12px 16px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.specs || '—'}</TableCell>
                                                        <TableCell style={{ fontSize: 12, color: PBI.muted, padding: '12px 16px' }}>
                                                            {typeof r.buyerId === 'object' ? (r.buyerId?.name || '—') : '—'}
                                                        </TableCell>
                                                        <TableCell style={{ fontSize: 12, color: PBI.muted, padding: '12px 16px' }}>
                                                            {r.desiredDeadline ? new Date(r.desiredDeadline).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                        </TableCell>
                                                        <TableCell style={{ fontSize: 12, color: PBI.muted, padding: '12px 16px' }}>
                                                            {new Date(r.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                        </TableCell>
                                                        <TableCell style={{ padding: '12px 16px' }}>
                                                            {requestMetaOf(r).action && (
                                                                <button
                                                                    {...quoteGate.disabledProps}
                                                                    onClick={() => setSelectedRequest(r)}
                                                                    /* A request the vendor still owes an answer on gets the
                                                                       accent button; one that is merely open for reference
                                                                       gets a quiet one, so the colour alone tells a vendor
                                                                       scanning the table where the work is. */
                                                                    style={needsVendorAction(r)
                                                                        ? { background: PBI.accent, color: PBI.white, border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }
                                                                        : { background: 'transparent', color: PBI.muted, border: `1px solid ${PBI.border}`, borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                                                >
                                                                    {requestMetaOf(r).action}
                                                                </button>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>

                            {/* ── Quotes Sent table ── */}
                            <Paper elevation={0} style={{ borderRadius: 12, border: `1px solid ${PBI.border}`, overflow: 'hidden' }}>
                                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${PBI.border}`, background: PBI.white, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography style={{ fontWeight: 700, fontSize: 14, color: PBI.text }}>Quotes Sent</Typography>
                                    <span style={{ fontSize: 12, color: PBI.muted }}>{quotes.length} total</span>
                                </div>
                                {quotes.length === 0 ? (
                                    <div style={{ padding: '32px 20px', textAlign: 'center', color: PBI.muted, fontSize: 14 }}>No quotes sent yet.</div>
                                ) : (
                                    <TableContainer>
                                        <Table style={{ minWidth: 600 }}>
                                            <TableHead>
                                                <TableRow style={{ background: PBI.navy }}>
                                                    {['Status', 'Total (UGX)', 'Currency', 'Valid Until', 'Order'].map((h) => (
                                                        <TableCell key={h} style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: 11, letterSpacing: 0.7, textTransform: 'uppercase', padding: '13px 16px', borderBottom: 'none' }}>{h}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {quotes.map((q, idx) => (
                                                    <TableRow key={q._id} className={classes.tableRow} style={{ background: idx % 2 === 0 ? PBI.white : '#f8fafc' }}>
                                                        <TableCell style={{ padding: '12px 16px' }}><QuoteStatusBadge status={q.status} /></TableCell>
                                                        <TableCell style={{ fontWeight: 700, color: PBI.text, padding: '12px 16px' }}>
                                                            {new Intl.NumberFormat('en-UG').format(q.totalAmount || 0)}
                                                        </TableCell>
                                                        <TableCell style={{ padding: '12px 16px' }}>
                                                            <span style={{ background: PBI.accentSoft, color: PBI.accent, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>{q.currency}</span>
                                                        </TableCell>
                                                        <TableCell style={{ fontSize: 12, color: PBI.muted, padding: '12px 16px' }}>
                                                            {q.validUntil ? new Date(q.validUntil).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                        </TableCell>
                                                        <TableCell style={{ padding: '12px 16px' }}>
                                                            {q.acceptedOrderId
                                                                ? <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>✓ Order created</span>
                                                                : <span style={{ color: PBI.muted, fontSize: 12 }}>Awaiting buyer</span>}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </>
                    )
                )}

                {/* ══════════════ ORDERS TAB ══════════════ */}
                {activeTab === TABS.ORDERS && <React.Fragment>

                {/* ── KPI row ── */}
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
                    <KpiCard label="Total Orders"  value={kpis.total}      accent={PBI.accent} />
                    <KpiCard label="Processing"    value={kpis.processing} accent="#f59e0b" />
                    <KpiCard label="Delivered"     value={kpis.delivered}  accent="#22c55e" />
                    <KpiCard label="Cancelled"     value={kpis.cancelled}  accent="#f43f5e" />
                </div>

                {orders.length === 0 && listFilters.active === 0 ? (
                    /* ── empty state ── */
                    <Box className={classes.noOrdersContainer}>
                        <svg className={classes.noOrdersIcon} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="250" cy="250" r="200" fill="#E8F4F8" opacity="0.5"/>
                            <circle cx="100" cy="100" r="20" fill="#0a578bff" opacity="0.2"/>
                            <circle cx="400" cy="120" r="15" fill="#17385aff" opacity="0.2"/>
                            <circle cx="380" cy="380" r="25" fill="#0a578bff" opacity="0.15"/>
                            <g transform="translate(150, 150)">
                                <path d="M20 40 L40 40 L60 120 L160 120 L180 60 L50 60"
                                      stroke="#0a578bff" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                <path d="M20 40 L10 10" stroke="#0a578bff" strokeWidth="8" strokeLinecap="round"/>
                                <circle cx="80" cy="140" r="12" fill="#17385aff"/>
                                <circle cx="140" cy="140" r="12" fill="#17385aff"/>
                                <rect x="70" y="70" width="30" height="40" rx="4" fill="#E8F4F8" stroke="#0a578bff" strokeWidth="3"/>
                                <rect x="110" y="65" width="35" height="45" rx="4" fill="#E8F4F8" stroke="#0a578bff" strokeWidth="3"/>
                            </g>
                            <g transform="translate(280, 200)">
                                <path d="M0 20 L40 0 L80 20 L80 80 L40 100 L0 80 Z"
                                      fill="#f8f9fa" stroke="#0a578bff" strokeWidth="4" strokeLinejoin="round"/>
                                <path d="M0 20 L40 40 L80 20" stroke="#0a578bff" strokeWidth="4" strokeLinejoin="round"/>
                                <path d="M40 40 L40 100" stroke="#0a578bff" strokeWidth="4"/>
                                <line x1="20" y1="50" x2="60" y2="80" stroke="#17385aff" strokeWidth="3" strokeLinecap="round"/>
                                <line x1="60" y1="50" x2="20" y2="80" stroke="#17385aff" strokeWidth="3" strokeLinecap="round"/>
                            </g>
                            <circle cx="120" cy="350" r="8" fill="#0a578bff" opacity="0.4">
                                <animate attributeName="cy" values="350;330;350" dur="2s" repeatCount="indefinite"/>
                            </circle>
                            <circle cx="370" cy="280" r="6" fill="#17385aff" opacity="0.4">
                                <animate attributeName="cy" values="280;260;280" dur="2.5s" repeatCount="indefinite"/>
                            </circle>
                        </svg>
                        <Typography style={{ fontSize: '1.8rem', fontWeight: 700, color: '#2d3748', marginBottom: 12 }}>
                            No Orders Yet
                        </Typography>
                        <Typography style={{ fontSize: '1rem', color: '#718096', maxWidth: 460, lineHeight: 1.7, marginBottom: 20 }}>
                            Your brand order history is empty. Once customers start placing orders, they'll appear here for you to manage and fulfill.
                        </Typography>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '12px 20px', background: 'rgba(255,255,255,0.85)',
                            borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }}>
                            <span style={{ fontSize: '1.3rem' }}>💡</span>
                            <Typography variant="body2" style={{ color: '#4a5568', fontWeight: 500 }}>
                                Make sure your products are active and visible to customers
                            </Typography>
                        </div>
                    </Box>
                ) : (
                    /* ── table panel ── */
                    <Paper elevation={0} style={{ borderRadius: 12, border: `1px solid ${PBI.border}`, overflow: 'hidden' }}>

                        {/* filter pills + count */}
                        <div style={{
                            padding: '14px 20px', borderBottom: `1px solid ${PBI.border}`,
                            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                            background: PBI.white,
                        }}>
                            <OrderListFilterBar
                                delivery={listFilters.delivery}
                                method={listFilters.method}
                                statuses={STATUS_PILLS}
                                onDelivery={listFilters.setDelivery}
                                onMethod={listFilters.setMethod}
                                style={{ padding: 0 }}
                            />
                            {/* A member who runs one branch has nothing to choose. */}
                            {brandLocations.hasBranches && brandLocations.locations.length > 1 && (
                                <AppSelect
                                    value={outletFilter}
                                    onChange={(e) => setOutletFilter(e.target.value)}
                                    aria-label="Filter by location"
                                    style={{
                                        marginLeft: 8, padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                        border: `1px solid ${PBI.border}`, background: PBI.bg, color: PBI.navy, cursor: 'pointer',
                                    }}
                                >
                                    <option value="">{isBranchScoped ? 'All my branches' : 'All locations'}</option>
                                    {brandLocations.locations.map((l) => (
                                        <option key={l.id} value={l.id}>{l.name}</option>
                                    ))}
                                </AppSelect>
                            )}
                            <span style={{ marginLeft: 'auto', fontSize: 12, color: PBI.muted, fontWeight: 500 }}>
                                {visibleOrders.length} order{visibleOrders.length !== 1 ? 's' : ''}
                            </span>
                        </div>

                        <TableContainer>
                            <Table style={{ minWidth: 720 }}>
                                <TableHead>
                                    <TableRow style={{ background: PBI.navy }}>
                                        {['Order ID', 'Customer', ...(showBranchColumn ? ['Branch'] : []), 'Items', 'Total (UGX)', 'Order Type', 'Payment', 'Paid', 'Status', 'Date', ''].map((h) => (
                                            <TableCell key={h} style={{
                                                color: 'rgba(255,255,255,0.85)', fontWeight: 700,
                                                fontSize: 11, letterSpacing: 0.7, textTransform: 'uppercase',
                                                padding: '13px 16px', borderBottom: 'none',
                                            }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleOrders.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={12} style={{ textAlign: 'center', padding: '28px 16px', color: PBI.muted, fontSize: 13 }}>
                                                No orders match this filter. Choose Any status, or remove the payment method, to see every order.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {visibleOrders.map((order, idx) => (
                                        <TableRow
                                            key={order._id}
                                            className={classes.tableRow}
                                            style={{ background: idx % 2 === 0 ? PBI.white : '#f8fafc' }}
                                        >
                                            <TableCell style={{ fontSize: 12, color: PBI.muted, fontFamily: 'monospace', padding: '12px 16px' }}>
                                                #{order._id.slice(-8).toUpperCase()}
                                            </TableCell>
                                            {/* Customer */}
                                            <TableCell style={{ fontSize: 12, color: PBI.text, padding: '12px 16px', maxWidth: 140 }}>
                                                <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {order.customerName || '—'}
                                                </div>
                                                {order.shippingAddress?.locationName?.name && (
                                                    <div style={{ fontSize: 11, color: PBI.muted, marginTop: 1 }}>
                                                        {order.shippingAddress.locationName.name}
                                                    </div>
                                                )}
                                            </TableCell>
                                            {showBranchColumn && (
                                                <TableCell style={{ fontSize: 12, color: PBI.text, padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                                    {orderLocationName(order, branchNameOf)}
                                                </TableCell>
                                            )}
                                            {/* Items count */}
                                            <TableCell style={{ fontSize: 12, color: PBI.text, padding: '12px 16px', textAlign: 'center' }}>
                                                <span style={{
                                                    background: '#f1f5f9', color: PBI.muted,
                                                    borderRadius: 20, padding: '2px 9px', fontWeight: 700, fontSize: 12,
                                                }}>
                                                    {order.orderItems?.length ?? 0}
                                                </span>
                                            </TableCell>
                                            <TableCell style={{ fontWeight: 700, color: PBI.text, padding: '12px 16px' }}>
                                                {new Intl.NumberFormat('en-UG').format(order.totalPrice || 0)}
                                            </TableCell>
                                            {/* Order Type */}
                                            <TableCell style={{ padding: '12px 16px' }}>
                                                {order.brandFlowType ? (
                                                    <span style={{
                                                        background: '#e0e7ff', color: '#3730a3',
                                                        borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 700,
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {order.brandFlowType}
                                                    </span>
                                                ) : <span style={{ color: PBI.muted, fontSize: 12 }}>—</span>}
                                            </TableCell>
                                            <TableCell style={{ padding: '12px 16px' }}>
                                                <span style={{
                                                    background: PBI.accentSoft, color: PBI.accent,
                                                    borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600,
                                                }}>
                                                    {order.paymentMethod || '—'}
                                                </span>
                                            </TableCell>
                                            {/* Paid badge */}
                                            <TableCell style={{ padding: '12px 16px' }}>
                                                <span style={{
                                                    background: order.isPaid ? '#dcfce7' : '#fee2e2',
                                                    color: order.isPaid ? '#166534' : '#991b1b',
                                                    borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 700,
                                                }}>
                                                    {order.isPaid ? 'Paid' : 'Unpaid'}
                                                </span>
                                            </TableCell>
                                            <TableCell style={{ padding: '12px 16px' }}>
                                                <StatusBadge status={order.delivery_status} />
                                            </TableCell>
                                            <TableCell style={{ fontSize: 12, color: PBI.muted, padding: '12px 16px' }}>
                                                {new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </TableCell>
                                            <TableCell style={{ padding: '12px 16px' }}>
                                                <button
                                                    onClick={() => handleManage(order._id)}
                                                    style={{
                                                        background: PBI.navy, color: PBI.white,
                                                        border: 'none', borderRadius: 7,
                                                        padding: '6px 14px', fontSize: 12, fontWeight: 600,
                                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                                                    }}
                                                >
                                                    Manage <span style={{ fontSize: 10 }}>→</span>
                                                </button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* pagination footer */}
                        <div style={{
                            borderTop: `1px solid ${PBI.border}`, background: PBI.white,
                            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                        }}>
                            <TablePagination
                                component="div"
                                count={numberOfPages * 10}
                                page={currentPage - 1}
                                onPageChange={(_, newPage) => dispatch(fetchBrandVendorOrders(vendorId, newPage + 1, 10, orderFilters))}
                                rowsPerPage={10}
                                rowsPerPageOptions={[10]}
                                style={{ fontSize: 12, color: PBI.muted }}
                            />
                        </div>
                    </Paper>
                )}
                </React.Fragment>}
                {/* end ORDERS tab */}

            </div>

            {/* ══════════════ RESPOND TO QUOTE MODAL ══════════════ */}
            <Modal
                open={!!selectedRequest}
                onClose={() => setSelectedRequest(null)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 200 }}
            >
                <Fade in={!!selectedRequest}>
                    <div className={classes.modalBox}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <Typography style={{ fontWeight: 700, fontSize: 16, color: PBI.text, margin: 0 }}>
                                Respond to: {selectedRequest?.title}
                            </Typography>
                            <span style={{ fontSize: 11, fontWeight: 700, color: PBI.muted, background: PBI.bg, borderRadius: 6, padding: '3px 10px', letterSpacing: 0.5 }}>QUOTE</span>
                        </div>
                        {selectedRequest && (
                            <div style={{ background: PBI.bg, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: PBI.muted, marginBottom: 16, lineHeight: 1.7 }}>
                                {selectedRequest.specs && <div><strong style={{ color: PBI.text }}>Buyer specs:</strong> {selectedRequest.specs}</div>}
                                {selectedRequest.desiredDeadline && <div><strong style={{ color: PBI.text }}>Buyer deadline:</strong> {new Date(selectedRequest.desiredDeadline).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>}
                                {selectedRequest.buyerId?.name && <div><strong style={{ color: PBI.text }}>Buyer:</strong> {selectedRequest.buyerId.name}</div>}
                            </div>
                        )}

                        <Typography style={{ fontWeight: 700, fontSize: 13, color: PBI.text, marginBottom: 8 }}>Line items</Typography>
                        {quoteForm.lineItems.map((li, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                <input
                                    placeholder="Description"
                                    value={li.description}
                                    onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                                    style={{ flex: 3, border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none' }}
                                />
                                <input
                                    type="number" min={1} placeholder="Qty"
                                    value={li.quantity}
                                    onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                                    style={{ flex: 1, border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none' }}
                                />
                                <input
                                    type="number" min={0} placeholder="Unit price"
                                    value={li.unitPrice}
                                    onChange={(e) => updateLineItem(idx, 'unitPrice', e.target.value)}
                                    style={{ flex: 2, border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none' }}
                                />
                                <button
                                    onClick={() => removeLineItem(idx)}
                                    disabled={quoteForm.lineItems.length === 1}
                                    style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, padding: '7px 11px', fontWeight: 700, cursor: 'pointer', fontSize: 14, opacity: quoteForm.lineItems.length === 1 ? 0.4 : 1 }}
                                >×</button>
                            </div>
                        ))}
                        <button
                            onClick={addLineItem}
                            style={{ background: PBI.bg, border: `1px dashed ${PBI.border}`, borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, color: PBI.muted, cursor: 'pointer', marginBottom: 12 }}
                        >
                            + Add line
                        </button>

                        {/* Shipping + VAT row */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: PBI.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 4 }}>Shipping / Freight ({quoteForm.currency})</label>
                                <input
                                    type="number" min={0} placeholder="0"
                                    value={quoteForm.shippingCost}
                                    onChange={(e) => setQuoteForm((f) => ({ ...f, shippingCost: e.target.value }))}
                                    style={{ width: '100%', border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: PBI.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 4 }}>VAT %</label>
                                <input
                                    type="number" min={0} max={100} placeholder="0"
                                    value={quoteForm.vatPct}
                                    onChange={(e) => setQuoteForm((f) => ({ ...f, vatPct: e.target.value }))}
                                    style={{ width: '100%', border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>

                        {/* Running total */}
                        <div style={{ background: PBI.navy, borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 3 }}>
                                <span>Subtotal</span><span>{quoteForm.currency} {new Intl.NumberFormat('en-UG').format(subtotal)}</span>
                            </div>
                            {Number(quoteForm.vatPct) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 3 }}>
                                    <span>VAT ({quoteForm.vatPct}%)</span><span>{quoteForm.currency} {new Intl.NumberFormat('en-UG').format(vatAmount)}</span>
                                </div>
                            )}
                            {Number(quoteForm.shippingCost) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 3 }}>
                                    <span>Shipping</span><span>{quoteForm.currency} {new Intl.NumberFormat('en-UG').format(Number(quoteForm.shippingCost))}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, color: PBI.white, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 6, marginTop: 4 }}>
                                <span>TOTAL</span><span>{quoteForm.currency} {new Intl.NumberFormat('en-UG').format(totalAmount)}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: PBI.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 4 }}>Valid until</label>
                                <input
                                    type="date"
                                    min={new Date().toISOString().slice(0, 10)}
                                    value={quoteForm.validUntil}
                                    onChange={(e) => setQuoteForm((f) => ({ ...f, validUntil: e.target.value }))}
                                    style={{ width: '100%', border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: PBI.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 4 }}>Currency</label>
                                <AppSelect
                                    value={quoteForm.currency}
                                    onChange={(e) => setQuoteForm((f) => ({ ...f, currency: e.target.value }))}
                                    style={{ width: '100%', border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                >
                                    <option value="UGX">UGX</option>
                                    <option value="USD">USD</option>
                                </AppSelect>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: PBI.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 4 }}>Lead Time</label>
                                <input
                                    placeholder="e.g. 5-7 working days"
                                    value={quoteForm.leadTime}
                                    onChange={(e) => setQuoteForm((f) => ({ ...f, leadTime: e.target.value }))}
                                    style={{ width: '100%', border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: PBI.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 4 }}>Payment Terms</label>
                            <AppSelect
                                value={quoteForm.paymentTerms}
                                onChange={(e) => setQuoteForm((f) => ({ ...f, paymentTerms: e.target.value }))}
                                style={{ width: '100%', border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                            >
                                <option value="">Select payment terms…</option>
                                <option value="100% upfront">100% upfront</option>
                                <option value="50% deposit, 50% on delivery">50% deposit, 50% on delivery</option>
                                <option value="Net 7">Net 7</option>
                                <option value="Net 14">Net 14</option>
                                <option value="Net 30">Net 30</option>
                                <option value="Net 60">Net 60</option>
                                <option value="On delivery">On delivery</option>
                            </AppSelect>
                        </div>

                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: PBI.muted, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 4 }}>Notes to buyer (optional)</label>
                            <textarea
                                rows={2}
                                value={quoteForm.notes}
                                onChange={(e) => setQuoteForm((f) => ({ ...f, notes: e.target.value }))}
                                style={{ width: '100%', border: `1px solid ${PBI.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setSelectedRequest(null)}
                                style={{ background: PBI.bg, border: `1px solid ${PBI.border}`, borderRadius: 7, padding: '8px 20px', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: PBI.text }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitQuoteResponse}
                                disabled={submittingQuote || !quoteGate.allowed || quoteForm.lineItems.some((li) => !li.description.trim())}
                                title={quoteGate.title}
                                style={{
                                    background: PBI.navy, color: PBI.white, border: 'none', borderRadius: 7,
                                    padding: '8px 22px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                    opacity: submittingQuote || quoteForm.lineItems.some((li) => !li.description.trim()) ? 0.6 : 1,
                                }}
                            >
                                {submittingQuote ? 'Sending…' : 'Send quote to buyer'}
                            </button>
                        </div>
                    </div>
                </Fade>
            </Modal>

        </div>
    );
};

export default BrandOrderTable;

